/**
 * @fileoverview Shared Utility Functions
 *
 * Purpose: Provides commonly used utility functions across the frontend.
 *
 * Exports:
 * - cn: Tailwind CSS class name merger
 * - callAll: Composes multiple functions into one
 * - getLocaleCurrency: Detects user currency from browser locale
 * - isMacPlatform / modKeyLabel: Platform-aware modifier-key display
 * - useSignOut: Hook for signing out via WorkOS AuthKit
 */

import { useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { CURRENCIES } from "@cvx/settings/billing/plans/schema";
import { useNavigate, useRouter } from "@tanstack/react-router";
import type { ClassValue } from "clsx";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { usePostHog } from "@/utils/posthog";

/**
 * Tailwind CSS classnames with support for conditional classes.
 * Widely used for Radix components.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a millisecond duration as a short human-readable string, e.g.
 * `"2h 14m"`, `"3m 12s"`, `"45s"`, or `"< 1s"`. Used by the job activity
 * breakdown to render agent processing time and chat engagement time.
 */
export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0s";
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 1) return "< 1s";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Returns a function that calls all of its arguments.
 */
export function callAll<Args extends unknown[]>(
  ...fns: (((...args: Args) => unknown) | undefined)[]
) {
  return (...args: Args) => fns.forEach((fn) => fn?.(...args));
}

/**
 * Locales.
 */
export function getLocaleCurrency() {
  return navigator.languages.includes("en-US")
    ? CURRENCIES.USD
    : CURRENCIES.EUR;
}

/**
 * True when the user is on a Mac (so keyboard hints should use ⌘ rather
 * than Ctrl). Prefers the modern `userAgentData.platform`, falling back to
 * the legacy `navigator.platform`. Guards against SSR/non-browser contexts.
 */
export function isMacPlatform() {
  if (typeof navigator === "undefined") return false;
  const platform =
    (
      navigator as Navigator & {
        userAgentData?: { platform?: string };
      }
    ).userAgentData?.platform ||
    navigator.platform ||
    navigator.userAgent ||
    "";
  return /mac/i.test(platform);
}

/**
 * The primary modifier key label for the current platform: "⌘" on Mac,
 * "Ctrl" elsewhere. Use for keyboard-shortcut hints so the displayed combo
 * matches what the user would actually press (the CommandPalette listener
 * already accepts both metaKey and ctrlKey).
 */
export function modKeyLabel() {
  return isMacPlatform() ? "⌘" : "Ctrl";
}

/**
 * Sign out hook using WorkOS AuthKit.
 * Clears the session, invalidates the router, and navigates to the login page.
 *
 * Returns a stable callback reference via useCallback.
 */
export const useSignOut = () => {
  const router = useRouter();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const posthog = usePostHog();

  return useCallback(async () => {
    posthog?.reset();
    await signOut();
    await router.invalidate();
    await navigate({ to: "/login" });
  }, [signOut, router, navigate, posthog]);
};
